import { BaseComponent } from '../lib/BaseComponent.js';
import { api, Extinguisher } from '../lib/api.js';
import { formatDate } from '../lib/dates.js';
import { router } from '../lib/router.js';
import { buildAppNav, APP_NAV_STYLES } from '../lib/app-nav.js';
import { Toast } from './shared/Toast.js';
import { Modal } from './shared/Modal.js';
import { buildExtinguisherForm, readExtinguisherForm } from '../lib/extinguisher-form.js';

/**
 * Extinguisher List Page Component
 * Enhanced list view with filtering and sorting
 */
export class ExtinguisherListPage extends BaseComponent {
  private isLoading = true;
  private extinguishers: Extinguisher[] = [];
  private filteredExtinguishers: Extinguisher[] = [];
  private errorMessage = '';
  private addModal: Modal | null = null;
  private importModal: Modal | null = null;

  // Filter state
  private filterCompliance: string = 'all';
  private filterStatus: string = 'all';
  private filterType: string = 'all';
  private filterActor: string = 'all';
  private urlApplied = false;

  // Sort state
  private sortBy: string = 'qr_code';
  private sortDirection: 'asc' | 'desc' = 'asc';

  // Search state
  private searchQuery: string = '';

  // Bulk operations state
  private selectedIds: Set<string> = new Set();
  private selectAll: boolean = false;

  protected async render(): Promise<void> {
    this.readFiltersFromUrl();
    this.addStyles(this.getCommonStyles());
    this.addStyles(this.getStyles());

    const container = this.createElement('div', { className: 'list-container' });

    // Header with navigation
    const header = this.createElement('header', { className: 'list-header' });
    const headerContent = this.createElement('div', { className: 'header-content' });

    const logo = this.createElement('h1', { className: 'logo' }, 'FireGuard');
    headerContent.appendChild(logo);

    const nav = buildAppNav((t, p, txt) => this.createElement(t as any, p as any, txt as any), '/extinguishers', () => this.handleLogout());
    headerContent.appendChild(nav);

    header.appendChild(headerContent);
    container.appendChild(header);

    // Main content
    const main = this.createElement('main', { className: 'list-main' });

    // Page title and actions
    const titleBar = this.createElement('div', { className: 'title-bar' });
    const title = this.createElement('h2', {}, 'All Fire Extinguishers');
    const addBtn = this.createElement('button', { className: 'btn-primary' }, '+ Add Extinguisher');
    addBtn.addEventListener('click', () => this.openAddExtinguisherFlow());
    const importBtn = this.createElement('button', { className: 'btn-secondary' }, '⬆ Import spreadsheet');
    importBtn.addEventListener('click', () => this.openImportModal());
    titleBar.appendChild(title);
    titleBar.appendChild(addBtn);
    titleBar.appendChild(importBtn);
    main.appendChild(titleBar);

    // Loading state
    if (this.isLoading) {
      const loading = this.createElement('div', { className: 'loading' }, 'Loading extinguishers...');
      main.appendChild(loading);
      container.appendChild(main);
      this.shadow.appendChild(container);

      // Fetch data
      await this.loadExtinguishers();
      return;
    }

    // Error state
    if (this.errorMessage) {
      const error = this.createElement('div', { className: 'error-message' }, this.errorMessage);
      main.appendChild(error);
      container.appendChild(main);
      this.shadow.appendChild(container);
      return;
    }

    // Controls: Search, Filter, Sort
    const controls = this.createControlsSection();
    main.appendChild(controls);

    // Summary stats
    const stats = this.createStatsSection();
    main.appendChild(stats);

    // Empty state
    if (this.filteredExtinguishers.length === 0) {
      const empty = this.createElement('div', { className: 'empty-state' });
      const emptyIcon = this.createElement('div', { className: 'empty-icon' }, '🔍');
      const emptyTitle = this.createElement('h3', {}, this.extinguishers.length === 0 ? 'No extinguishers yet' : 'No matching extinguishers');
      const emptyText = this.createElement('p', {}, this.extinguishers.length === 0 ? 'Add your first fire extinguisher to get started' : 'Try adjusting your filters');
      empty.appendChild(emptyIcon);
      empty.appendChild(emptyTitle);
      empty.appendChild(emptyText);
      main.appendChild(empty);
      container.appendChild(main);
      this.shadow.appendChild(container);
      return;
    }

    // Extinguishers table
    const table = this.createExtinguishersTable();
    main.appendChild(table);

    container.appendChild(main);
    this.shadow.appendChild(container);
  }

  private createControlsSection(): HTMLElement {
    const controls = this.createElement('div', { className: 'controls' });

    // Search bar
    const searchContainer = this.createElement('div', { className: 'search-container' });
    const searchInput = this.createElement('input', {
      type: 'text',
      placeholder: 'Search by QR code or location...',
      className: 'search-input',
    }) as HTMLInputElement;
    searchInput.value = this.searchQuery;
    searchInput.addEventListener('input', (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value;
      this.applyFilters();
    });
    searchContainer.appendChild(searchInput);
    controls.appendChild(searchContainer);

    // Filters
    const filters = this.createElement('div', { className: 'filters' });

    // Compliance filter
    const complianceFilter = this.createElement('select', { className: 'filter-select' });
    complianceFilter.addEventListener('change', (e) => {
      this.filterCompliance = (e.target as HTMLSelectElement).value;
      this.applyFilters();
    });
    ['All Compliance', 'Compliant', 'Warning', 'Overdue'].forEach((label, i) => {
      const option = this.createElement('option', {
        value: ['all', 'compliant', 'warning', 'overdue'][i],
      }, label);
      complianceFilter.appendChild(option);
    });
    complianceFilter.value = this.filterCompliance;
    filters.appendChild(complianceFilter);

    // Status filter
    const statusFilter = this.createElement('select', { className: 'filter-select' });
    statusFilter.addEventListener('change', (e) => {
      this.filterStatus = (e.target as HTMLSelectElement).value;
      this.applyFilters();
    });
    ['All Status', 'Active', 'Removed', 'Replaced'].forEach((label, i) => {
      const option = this.createElement('option', {
        value: ['all', 'active', 'removed', 'replaced'][i],
      }, label);
      statusFilter.appendChild(option);
    });
    statusFilter.value = this.filterStatus;
    filters.appendChild(statusFilter);

    // Type filter
    const typeFilter = this.createElement('select', { className: 'filter-select' });
    typeFilter.addEventListener('change', (e) => {
      this.filterType = (e.target as HTMLSelectElement).value;
      this.applyFilters();
    });
    const types = ['all', ...new Set(this.extinguishers.map(e => e.extinguisher_type))];
    types.forEach(type => {
      const option = this.createElement('option', {
        value: type,
      }, type === 'all' ? 'All Types' : type);
      typeFilter.appendChild(option);
    });
    filters.appendChild(typeFilter);

    // Who has to act filter — separates work your own staff can clear today
    // from work that needs a contractor scheduled and paid.
    const actorFilter = this.createElement('select', { className: 'filter-select' });
    actorFilter.addEventListener('change', (e) => {
      this.filterActor = (e.target as HTMLSelectElement).value;
      this.applyFilters();
    });
    [
      { value: 'all', label: 'Anyone can act' },
      { value: 'staff', label: 'Your staff can fix' },
      { value: 'contractor', label: 'Needs a contractor' },
    ].forEach(({ value, label }) => {
      actorFilter.appendChild(this.createElement('option', { value }, label));
    });
    actorFilter.value = this.filterActor;
    filters.appendChild(actorFilter);

    controls.appendChild(filters);

    // Sort controls
    const sortControls = this.createElement('div', { className: 'sort-controls' });
    const sortLabel = this.createElement('span', { className: 'sort-label' }, 'Sort by:');
    sortControls.appendChild(sortLabel);

    const sortSelect = this.createElement('select', { className: 'sort-select' });
    sortSelect.addEventListener('change', (e) => {
      this.sortBy = (e.target as HTMLSelectElement).value;
      this.applyFilters();
    });
    [
      { value: 'qr_code', label: 'QR Code' },
      { value: 'compliance_status', label: 'Compliance' },
      { value: 'extinguisher_type', label: 'Type' },
      { value: 'size_lbs', label: 'Size' },
      { value: 'install_date', label: 'Install Date' },
      { value: 'last_monthly_inspection', label: 'Last Inspection' },
    ].forEach(({ value, label }) => {
      const option = this.createElement('option', { value }, label);
      sortSelect.appendChild(option);
    });
    sortControls.appendChild(sortSelect);

    const sortDirBtn = this.createElement('button', { className: 'sort-dir-btn' }, '↑');
    sortDirBtn.addEventListener('click', () => {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      sortDirBtn.textContent = this.sortDirection === 'asc' ? '↑' : '↓';
      this.applyFilters();
    });
    sortControls.appendChild(sortDirBtn);

    controls.appendChild(sortControls);

    return controls;
  }

  /**
   * Honor filters passed in the URL. The dashboard links to
   * /extinguishers?compliance_status=overdue and email alerts link to
   * ?actor=staff; without this the drill-down landed on the unfiltered list
   * and the operator had to re-find the units they had just clicked through for.
   */
  private readFiltersFromUrl(): void {
    if (this.urlApplied) return;
    const params = new URLSearchParams(window.location.search);
    const compliance = params.get('compliance_status') || params.get('compliance');
    if (compliance && ['compliant', 'warning', 'overdue'].includes(compliance)) {
      this.filterCompliance = compliance;
    }
    const actor = params.get('actor');
    if (actor && ['staff', 'contractor'].includes(actor)) {
      this.filterActor = actor;
    }
    const status = params.get('status');
    if (status && ['active', 'removed', 'replaced'].includes(status)) {
      this.filterStatus = status;
    }
    const search = params.get('q');
    if (search) this.searchQuery = search;
    this.urlApplied = true;
  }

  private createStatsSection(): HTMLElement {
    const stats = this.createElement('div', { className: 'stats' });

    const total = this.filteredExtinguishers.length;
    const compliant = this.filteredExtinguishers.filter(e => e.compliance_status === 'compliant').length;
    const warning = this.filteredExtinguishers.filter(e => e.compliance_status === 'warning').length;
    const overdue = this.filteredExtinguishers.filter(e => e.compliance_status === 'overdue').length;

    // Split the work by who has to do it. "42 overdue" is not actionable;
    // "30 your staff can clear today, 12 need a contractor" is.
    const needsStaff = this.filteredExtinguishers.filter(e => e.compliance_actor === 'staff').length;
    const needsContractor = this.filteredExtinguishers.filter(e => e.compliance_actor === 'contractor').length;

    const statItems: Array<{ label: string; value: number; className: string; actor?: string }> = [
      { label: 'Total', value: total, className: 'stat-total' },
      { label: 'Compliant', value: compliant, className: 'stat-compliant' },
      { label: 'Warning', value: warning, className: 'stat-warning' },
      { label: 'Overdue', value: overdue, className: 'stat-overdue' },
      { label: 'Your staff can fix', value: needsStaff, className: 'stat-staff', actor: 'staff' },
      { label: 'Needs a contractor', value: needsContractor, className: 'stat-contractor', actor: 'contractor' },
    ];

    statItems.forEach(({ label, value, className, actor }) => {
      const statCard = this.createElement('div', { className: `stat-card ${className}` });
      const statValue = this.createElement('div', { className: 'stat-value' }, value.toString());
      const statLabel = this.createElement('div', { className: 'stat-label' }, label);
      statCard.appendChild(statValue);
      statCard.appendChild(statLabel);
      if (actor) {
        statCard.classList.add('stat-clickable');
        statCard.addEventListener('click', () => {
          // Toggle: clicking the active card clears the filter.
          this.filterActor = this.filterActor === actor ? 'all' : actor;
          this.applyFilters();
        });
      }
      stats.appendChild(statCard);
    });

    return stats;
  }

  private createExtinguishersTable(): HTMLElement {
    const tableContainer = this.createElement('div', { className: 'table-container' });

    // Bulk actions bar
    if (this.selectedIds.size > 0) {
      const bulkBar = this.createBulkActionsBar();
      tableContainer.appendChild(bulkBar);
    }

    const table = this.createElement('table', { className: 'extinguishers-table' });

    // Header
    const thead = this.createElement('thead', {});
    const headerRow = this.createElement('tr', {});

    // Select All checkbox
    const selectAllTh = this.createElement('th', {});
    const selectAllCheckbox = this.createElement('input', {
      type: 'checkbox',
      className: 'bulk-checkbox',
    }) as HTMLInputElement;
    selectAllCheckbox.checked = this.selectAll;
    selectAllCheckbox.addEventListener('change', (e) => {
      this.selectAll = (e.target as HTMLInputElement).checked;
      if (this.selectAll) {
        this.filteredExtinguishers.forEach(ext => this.selectedIds.add(ext.id));
      } else {
        this.selectedIds.clear();
      }
      this.shadow.innerHTML = '';
      this.render();
    });
    selectAllTh.appendChild(selectAllCheckbox);
    headerRow.appendChild(selectAllTh);

    ['QR Code', 'Type', 'Size', 'Location', 'Compliance', 'Last Inspection', 'Actions'].forEach(text => {
      const th = this.createElement('th', {}, text);
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);

    // Body
    const tbody = this.createElement('tbody', {});
    this.filteredExtinguishers.forEach(ext => {
      const row = this.createElement('tr', { className: 'extinguisher-row' });

      // Checkbox
      const checkboxCell = this.createElement('td', {});
      const checkbox = this.createElement('input', {
        type: 'checkbox',
        className: 'bulk-checkbox',
      }) as HTMLInputElement;
      checkbox.checked = this.selectedIds.has(ext.id);
      checkbox.addEventListener('change', (e) => {
        if ((e.target as HTMLInputElement).checked) {
          this.selectedIds.add(ext.id);
        } else {
          this.selectedIds.delete(ext.id);
          this.selectAll = false;
        }
        this.shadow.innerHTML = '';
        this.render();
      });
      checkboxCell.appendChild(checkbox);
      row.appendChild(checkboxCell);

      // QR Code
      const qrCell = this.createElement('td', { className: 'cell-qr' });
      const qrLink = this.createElement('a', {
        href: `/extinguishers/${ext.id}`,
        className: 'qr-link',
      }, ext.qr_code);
      qrCell.appendChild(qrLink);
      row.appendChild(qrCell);

      // Type
      const typeCell = this.createElement('td', {}, ext.extinguisher_type);
      row.appendChild(typeCell);

      // Size
      const sizeCell = this.createElement('td', {}, `${ext.size_lbs} lbs`);
      row.appendChild(sizeCell);

      // Location
      const locationCell = this.createElement('td', {}, ext.room_area || 'N/A');
      row.appendChild(locationCell);

      // Compliance Status
      const complianceCell = this.createElement('td', {});
      const badge = this.createElement('span', {
        className: `status-badge status-${ext.compliance_status}`,
      }, ext.compliance_status.toUpperCase());
      complianceCell.appendChild(badge);
      if (ext.compliance_actor === 'staff' || ext.compliance_actor === 'contractor') {
        const who = this.createElement('span', {
          className: `actor-badge actor-${ext.compliance_actor}`,
        }, ext.compliance_actor === 'staff' ? 'Your staff' : 'Contractor');
        complianceCell.appendChild(who);
      }
      if (ext.compliance_summary) {
        const why = this.createElement('div', { className: 'compliance-why' }, ext.compliance_summary);
        complianceCell.appendChild(why);
      }
      row.appendChild(complianceCell);

      // Last Inspection
      const inspectionCell = this.createElement('td', {});
      if (ext.last_monthly_inspection) {
        inspectionCell.textContent = formatDate(ext.last_monthly_inspection);
      } else {
        inspectionCell.textContent = 'Never';
        inspectionCell.classList.add('text-muted');
      }
      row.appendChild(inspectionCell);

      // Actions
      const actionsCell = this.createElement('td', { className: 'cell-actions' });
      // The buttons live in a wrapper rather than on the cell itself: a <td>
      // with display:flex leaves table layout and renders its own box.
      const actionsInner = this.createElement('div', { className: 'actions-inner' });
      const viewBtn = this.createElement('button', { className: 'btn-link' }, 'View');
      viewBtn.addEventListener('click', () => router.navigate(`/extinguishers/${ext.id}`));
      const inspectBtn = this.createElement('button', { className: 'btn-link' }, 'Inspect');
      inspectBtn.addEventListener('click', () => router.navigate(`/extinguishers/${ext.id}/inspect`));
      actionsInner.appendChild(viewBtn);
      actionsInner.appendChild(inspectBtn);
      actionsCell.appendChild(actionsInner);
      row.appendChild(actionsCell);

      tbody.appendChild(row);
    });
    table.appendChild(tbody);

    tableContainer.appendChild(table);
    return tableContainer;
  }

  private applyFilters(): void {
    let filtered = [...this.extinguishers];

    // Apply search
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(e =>
        e.qr_code.toLowerCase().includes(query) ||
        e.room_area?.toLowerCase().includes(query)
      );
    }

    // Apply compliance filter
    if (this.filterCompliance !== 'all') {
      filtered = filtered.filter(e => e.compliance_status === this.filterCompliance);
    }

    // Apply "who has to act" filter
    if (this.filterActor !== 'all') {
      filtered = filtered.filter(e => e.compliance_actor === this.filterActor);
    }

    // Apply status filter
    if (this.filterStatus !== 'all') {
      filtered = filtered.filter(e => e.status === this.filterStatus);
    }

    // Apply type filter
    if (this.filterType !== 'all') {
      filtered = filtered.filter(e => e.extinguisher_type === this.filterType);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal: any = a[this.sortBy as keyof Extinguisher];
      let bVal: any = b[this.sortBy as keyof Extinguisher];

      // Handle null values
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      if (typeof aVal === 'string') {
        return this.sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return this.sortDirection === 'asc'
        ? aVal - bVal
        : bVal - aVal;
    });

    this.filteredExtinguishers = filtered;
    this.shadow.innerHTML = '';
    this.render();
  }

  private async loadExtinguishers(): Promise<void> {
    try {
      const response = await api.getExtinguishers();
      this.extinguishers = response.extinguishers;
      this.isLoading = false;
      // Run the filters rather than assigning the raw list. A filter preset
      // before the data arrived — from the URL, or from a card the operator
      // clicked on the dashboard — was silently discarded here: the dropdown
      // read "Overdue" while the table below it showed every unit.
      this.applyFilters();
    } catch (error) {
      this.isLoading = false;
      this.errorMessage = (error as Error).message || 'Failed to load extinguishers';
      this.shadow.innerHTML = '';
      await this.render();
    }
  }

  /**
   * Create bulk actions bar
   */
  private createBulkActionsBar(): HTMLElement {
    const bar = this.createElement('div', { className: 'bulk-actions-bar' });

    const count = this.createElement('span', { className: 'bulk-count' }, `${this.selectedIds.size} selected`);
    bar.appendChild(count);

    const actions = this.createElement('div', { className: 'bulk-actions' });

    const activateBtn = this.createElement('button', { className: 'btn-secondary btn-small' }, 'Activate');
    activateBtn.addEventListener('click', () => this.bulkActivate());
    actions.appendChild(activateBtn);

    const deactivateBtn = this.createElement('button', { className: 'btn-secondary btn-small' }, 'Deactivate');
    deactivateBtn.addEventListener('click', () => this.bulkDeactivate());
    actions.appendChild(deactivateBtn);

    const deleteBtn = this.createElement('button', { className: 'btn-danger btn-small' }, 'Delete');
    deleteBtn.addEventListener('click', () => this.bulkDelete());
    actions.appendChild(deleteBtn);

    bar.appendChild(actions);

    return bar;
  }

  /**
   * Bulk activate selected extinguishers
   */
  private async bulkActivate(): Promise<void> {
    if (this.selectedIds.size === 0) return;

    const confirmed = confirm(`Activate ${this.selectedIds.size} extinguisher(s)?`);
    if (!confirmed) return;

    try {
      const promises = Array.from(this.selectedIds).map(id =>
        api.updateExtinguisher(id, { status: 'active' })
      );

      await Promise.all(promises);

      Toast.success(`Activated ${this.selectedIds.size} extinguisher(s)!`);
      this.selectedIds.clear();
      this.selectAll = false;

      // Reload data
      await this.loadExtinguishers();
    } catch (error) {
      Toast.error((error as Error).message || 'Failed to activate extinguishers');
    }
  }

  /**
   * Bulk deactivate selected extinguishers
   */
  private async bulkDeactivate(): Promise<void> {
    if (this.selectedIds.size === 0) return;

    const confirmed = confirm(`Deactivate ${this.selectedIds.size} extinguisher(s)?`);
    if (!confirmed) return;

    try {
      const promises = Array.from(this.selectedIds).map(id =>
        api.updateExtinguisher(id, { status: 'removed' })
      );

      await Promise.all(promises);

      Toast.success(`Deactivated ${this.selectedIds.size} extinguisher(s)!`);
      this.selectedIds.clear();
      this.selectAll = false;

      // Reload data
      await this.loadExtinguishers();
    } catch (error) {
      Toast.error((error as Error).message || 'Failed to deactivate extinguishers');
    }
  }

  /**
   * Bulk delete selected extinguishers
   */
  private async bulkDelete(): Promise<void> {
    if (this.selectedIds.size === 0) return;

    const confirmed = confirm(
      `Delete ${this.selectedIds.size} extinguisher(s)?\n\nThis action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      const promises = Array.from(this.selectedIds).map(id =>
        api.deleteExtinguisher(id)
      );

      await Promise.all(promises);

      Toast.success(`Deleted ${this.selectedIds.size} extinguisher(s)!`);
      this.selectedIds.clear();
      this.selectAll = false;

      // Reload data
      await this.loadExtinguishers();
    } catch (error) {
      Toast.error((error as Error).message || 'Failed to delete extinguishers');
    }
  }

  private handleLogout(): void {
    api.logout();
    router.navigate('/login');
  }

  private getStyles(): string {
    return `
      ${APP_NAV_STYLES}

      .list-container {
        min-height: 100vh;
        background: var(--gray-50);
      }

      .list-header {
        background: white;
        border-bottom: 1px solid var(--gray-200);
        padding: var(--spacing-md) var(--spacing-lg);
      }

      .header-content {
        max-width: 1400px;
        margin: 0 auto;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .logo {
        font-size: 24px;
        font-weight: 700;
        color: var(--danger);
        margin: 0;
      }

      .nav {
        display: flex;
        gap: var(--spacing-md);
      }

      .list-main {
        max-width: 1400px;
        margin: 0 auto;
        padding: var(--spacing-lg);
      }

      .title-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--spacing-lg);
      }

      .title-bar h2 {
        font-size: 28px;
        font-weight: 600;
        color: var(--gray-900);
      }

      .btn-primary {
        padding: 12px 24px;
        background: var(--primary);
        color: white;
        border: none;
        border-radius: var(--border-radius);
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;
      }

      .btn-primary:hover {
        background: var(--primary-dark);
      }

      .btn-secondary {
        padding: 10px 20px;
        background: white;
        color: var(--gray-700);
        border: 1px solid var(--gray-300);
        border-radius: var(--border-radius);
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
      }

      .btn-secondary:hover {
        background: var(--gray-50);
      }

      .btn-link {
        background: transparent;
        border: none;
        color: var(--primary);
        cursor: pointer;
        font-size: 14px;
        padding: 4px 8px;
        text-decoration: underline;
      }

      .btn-link:hover {
        color: var(--primary-dark);
      }

      .loading {
        text-align: center;
        padding: 40px;
        color: var(--gray-500);
      }

      .error-message {
        background: #fef2f2;
        border: 1px solid #fecaca;
        color: #dc2626;
        padding: var(--spacing-md);
        border-radius: var(--border-radius);
        margin-bottom: var(--spacing-md);
      }

      .empty-state {
        text-align: center;
        padding: 60px 20px;
        background: white;
        border-radius: var(--border-radius);
        border: 2px dashed var(--gray-300);
      }

      .empty-icon {
        font-size: 64px;
        margin-bottom: var(--spacing-md);
      }

      .empty-state h3 {
        font-size: 20px;
        color: var(--gray-900);
        margin-bottom: var(--spacing-sm);
      }

      .empty-state p {
        color: var(--gray-500);
      }

      .controls {
        background: white;
        border-radius: var(--border-radius);
        padding: var(--spacing-lg);
        margin-bottom: var(--spacing-lg);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .search-container {
        margin-bottom: var(--spacing-md);
      }

      .search-input {
        width: 100%;
        padding: 12px;
        border: 1px solid var(--gray-300);
        border-radius: var(--border-radius);
        font-size: 16px;
      }

      .search-input:focus {
        outline: none;
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      .filters {
        display: flex;
        gap: var(--spacing-md);
        margin-bottom: var(--spacing-md);
        flex-wrap: wrap;
      }

      .filter-select, .sort-select {
        padding: 8px 12px;
        border: 1px solid var(--gray-300);
        border-radius: var(--border-radius);
        font-size: 14px;
        background: white;
        cursor: pointer;
      }

      .sort-controls {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
      }

      .sort-label {
        font-size: 14px;
        color: var(--gray-700);
        font-weight: 500;
      }

      .sort-dir-btn {
        padding: 8px 12px;
        background: white;
        border: 1px solid var(--gray-300);
        border-radius: var(--border-radius);
        cursor: pointer;
        font-size: 16px;
      }

      .sort-dir-btn:hover {
        background: var(--gray-50);
      }

      .stats {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: var(--spacing-md);
        margin-bottom: var(--spacing-lg);
      }

      .stat-card {
        background: white;
        border-radius: var(--border-radius);
        padding: var(--spacing-lg);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        text-align: center;
      }

      .stat-value {
        font-size: 32px;
        font-weight: 700;
        margin-bottom: var(--spacing-sm);
      }

      .stat-label {
        font-size: 14px;
        color: var(--gray-500);
      }

      .stat-compliant .stat-value {
        color: var(--success);
      }

      .stat-warning .stat-value {
        color: #f59e0b;
      }

      .stat-overdue .stat-value {
        color: var(--danger);
      }

      .stat-staff .stat-value {
        color: #1d4ed8;
      }

      .stat-contractor .stat-value {
        color: #b45309;
      }

      .stat-clickable {
        cursor: pointer;
        transition: box-shadow 0.15s, transform 0.15s;
      }

      .stat-clickable:hover {
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
        transform: translateY(-1px);
      }

      .table-container {
        background: white;
        border-radius: var(--border-radius);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        overflow-x: auto;
      }

      .bulk-actions-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--spacing-md);
        background: #f3f4f6;
        border-bottom: 1px solid var(--gray-200);
      }

      .bulk-count {
        font-size: 14px;
        font-weight: 600;
        color: var(--gray-700);
      }

      .bulk-actions {
        display: flex;
        gap: var(--spacing-sm);
      }

      .btn-small {
        padding: 6px 12px;
        font-size: 13px;
      }

      .bulk-checkbox {
        width: 18px;
        height: 18px;
        cursor: pointer;
      }

      .extinguishers-table {
        width: 100%;
        border-collapse: collapse;
      }

      .extinguishers-table th {
        text-align: left;
        padding: var(--spacing-md);
        border-bottom: 2px solid var(--gray-200);
        font-size: 14px;
        font-weight: 600;
        color: var(--gray-700);
        background: var(--gray-50);
      }

      .extinguishers-table td {
        padding: var(--spacing-md);
        border-bottom: 1px solid var(--gray-200);
        font-size: 14px;
        /* The compliance cell now carries a reason line under the badge, so
           rows are two lines tall. Top-align every cell or the shorter ones
           float in the middle of the row. */
        vertical-align: top;
      }

      .extinguisher-row:hover {
        background: var(--gray-50);
      }

      .cell-qr {
        font-weight: 600;
      }

      .qr-link {
        color: var(--primary);
        text-decoration: none;
      }

      .qr-link:hover {
        text-decoration: underline;
      }

      .cell-actions .actions-inner {
        display: flex;
        gap: var(--spacing-sm);
      }

      .text-muted {
        color: var(--gray-400);
      }

      .status-badge {
        display: inline-block;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 600;
        color: white;
      }

      .status-compliant {
        background: var(--success);
      }

      .status-warning {
        background: #f59e0b;
      }

      .status-overdue {
        background: var(--danger);
      }

      /* Who has to act — staff work is free and can happen today,
         contractor work costs money and has to be scheduled. */
      .actor-badge {
        display: inline-block;
        margin-left: 6px;
        padding: 3px 7px;
        border-radius: 4px;
        font-size: 11px;
        font-weight: 600;
        border: 1px solid;
      }

      .actor-staff {
        color: #1d4ed8;
        background: #eff6ff;
        border-color: #bfdbfe;
      }

      .actor-contractor {
        color: #92400e;
        background: #fffbeb;
        border-color: #fde68a;
      }

      .compliance-why {
        margin-top: 4px;
        font-size: 12px;
        line-height: 1.4;
        color: var(--text-secondary, #6b7280);
        max-width: 320px;
      }

      /* Add Extinguisher Form Styles */
      .add-extinguisher-form {
        display: flex;
        flex-direction: column;
        gap: 15px;
        padding: 20px;
      }

      .form-group {
        display: flex;
        flex-direction: column;
        gap: 5px;
      }

      .form-group label {
        font-weight: 600;
        font-size: 14px;
        color: var(--gray-900);
      }

      .form-control {
        padding: 10px;
        border: 1px solid var(--gray-300);
        border-radius: var(--border-radius);
        font-size: 14px;
        font-family: inherit;
        background: white;
        color: var(--gray-900);
      }

      .form-control:focus {
        outline: none;
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      .form-buttons {
        display: flex;
        gap: 10px;
        margin-top: 10px;
        justify-content: flex-end;
      }

      .btn-danger {
        padding: 8px 16px;
        background: var(--danger);
        color: white;
        border: none;
        border-radius: var(--border-radius);
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;
      }

      .btn-danger:hover {
        background: #dc2626;
      }

      @media (max-width: 768px) {
        .filters {
          flex-direction: column;
        }

        .filter-select {
          width: 100%;
        }

        .stats {
          grid-template-columns: repeat(2, 1fr);
        }

        .table-container {
          overflow-x: scroll;
        }

        .nav {
          flex-direction: column;
          gap: var(--spacing-sm);
        }
      }
    `;
  }

  /**
   * Open add extinguisher modal
   */
  /**
   * Spreadsheet import. Always previews first: the user sees exactly what would be
   * created, what is already there and which rows were rejected and why, before
   * anything is written.
   */
  private async openImportModal(): Promise<void> {
    if (!this.importModal) {
      this.importModal = document.createElement('app-modal') as Modal;
      document.body.appendChild(this.importModal);
    }
    const modal = this.importModal;

    const wrap = document.createElement('div');
    const style = document.createElement('style');
    style.textContent = `
      .imp { display: grid; gap: 14px; }
      .imp p { margin: 0; font-size: 14px; color: #374151; line-height: 1.5; }
      .imp textarea { width: 100%; min-height: 150px; box-sizing: border-box; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 6px; font: 13px/1.5 ui-monospace, Menlo, monospace; }
      .imp-row { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
      .imp-link { color: #3b82f6; font-size: 14px; text-decoration: none; cursor: pointer; background: none; border: none; padding: 0; font-family: inherit; }
      .imp-summary { border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px; background: #f9fafb; font-size: 14px; }
      .imp-summary strong { font-size: 16px; }
      .imp-good { color: #15803d; }
      .imp-warn { color: #b45309; }
      .imp-bad { color: #b91c1c; }
      .imp-problems { max-height: 190px; overflow: auto; border: 1px solid #fecaca; border-radius: 6px; background: #fef2f2; padding: 10px 12px; font-size: 13px; }
      .imp-problems div { padding: 3px 0; border-bottom: 1px solid #fee2e2; }
      .imp-problems div:last-child { border-bottom: 0; }
      /* These rows do import; they just lost something. Amber, not red. */
      .imp-notes { border-color: #fde68a; background: #fffbeb; color: #92400e; }
      .imp-notes div { border-bottom-color: #fef3c7; }
    `;
    wrap.appendChild(style);

    const box = document.createElement('div');
    box.className = 'imp';
    const intro = document.createElement('p');
    intro.textContent = 'Paste the rows from your spreadsheet, or choose a CSV file. Column names are matched loosely, so a contractor export usually works as-is.';
    box.appendChild(intro);

    const controls = document.createElement('div');
    controls.className = 'imp-row';
    const file = document.createElement('input');
    file.type = 'file';
    file.accept = '.csv,text/csv,text/plain';
    const tpl = document.createElement('a');
    tpl.className = 'imp-link';
    tpl.textContent = 'Download the template';
    tpl.href = '#';
    tpl.addEventListener('click', async (e) => {
      e.preventDefault();
      try {
        const res = await fetch('/api/extinguishers/import/template', { headers: { Authorization: `Bearer ${api.getToken()}` } });
        const text = await res.text();
        const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'fireguard-import-template.csv';
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        Toast.error('Could not download the template');
      }
    });
    controls.appendChild(file);
    controls.appendChild(tpl);
    box.appendChild(controls);

    const textarea = document.createElement('textarea');
    textarea.placeholder = 'qr_code,location,room_area,type,agent,size_lbs,install_date\nEXT-001,Main Plant,Shop floor,ABC,Dry chemical,10,2024-03-15';
    box.appendChild(textarea);
    file.addEventListener('change', async () => {
      const f = file.files?.[0];
      if (!f) return;
      if (f.size > 1_000_000) {
        Toast.error('That file is over 1 MB. Split it and import in parts.');
        return;
      }
      textarea.value = await f.text();
      void preview();
    });

    const summary = document.createElement('div');
    summary.className = 'imp-summary';
    summary.style.display = 'none';
    box.appendChild(summary);
    wrap.appendChild(box);

    let canCommit = false;
    const esc = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;');

    const preview = async () => {
      const csv = textarea.value.trim();
      canCommit = false;
      commitBtn.setAttribute('disabled', 'true');
      if (!csv) {
        summary.style.display = 'none';
        return;
      }
      summary.style.display = 'block';
      summary.textContent = 'Checking…';
      try {
        const r = await api.importExtinguishers(csv, false);
        const parts: string[] = [];
        parts.push(`<strong class="${r.willCreate ? 'imp-good' : 'imp-warn'}">${r.willCreate} to import</strong>`);
        if (r.alreadyPresent) parts.push(`<span class="imp-warn">${r.alreadyPresent} already in FireGuard (skipped)</span>`);
        if (r.rejected) parts.push(`<span class="imp-bad">${r.rejected} rejected</span>`);
        let html = parts.join(' &middot; ');
        if (r.newLocations.length) html += `<div style="margin-top:8px">New locations to create: ${r.newLocations.map(esc).join(', ')}</div>`;
        if (r.ignoredColumns.length) html += `<div style="margin-top:8px" class="imp-warn">Columns not recognised and ignored: ${r.ignoredColumns.map(esc).join(', ')}</div>`;
        if (r.planLimit) html += `<div style="margin-top:8px" class="imp-bad">This would put you ${r.planLimit.overBy} over your plan limit of ${r.planLimit.limit}. <a href="/billing">Upgrade</a> first.</div>`;
        if (r.warnings?.length) {
          html += `<div class="imp-problems imp-notes" style="margin-top:10px">${r.warnings.map((w) => `<div>Line ${w.line}, ${esc(w.field)}${w.value ? ` &ldquo;${esc(w.value)}&rdquo;` : ''}: ${esc(w.message)}</div>`).join('')}</div>`;
        }
        if (r.problems.length) {
          html += `<div class="imp-problems" style="margin-top:10px">${r.problems.map((p) => `<div>Line ${p.line}, ${esc(p.field)}${p.value ? ` &ldquo;${esc(p.value)}&rdquo;` : ''}: ${esc(p.message)}</div>`).join('')}</div>`;
        }
        summary.innerHTML = html;
        canCommit = r.willCreate > 0 && !r.planLimit;
        if (canCommit) commitBtn.removeAttribute('disabled');
      } catch (err) {
        summary.innerHTML = `<span class="imp-bad">${esc((err as Error).message || 'Could not read that file')}</span>`;
      }
    };

    let debounce: number | undefined;
    textarea.addEventListener('input', () => {
      window.clearTimeout(debounce);
      debounce = window.setTimeout(() => void preview(), 400);
    });

    const footer = document.createElement('div');
    footer.style.display = 'flex';
    footer.style.gap = '12px';
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'btn-secondary';
    cancel.textContent = 'Cancel';
    cancel.addEventListener('click', () => modal.close());
    const commitBtn = document.createElement('button');
    commitBtn.type = 'button';
    commitBtn.className = 'btn-primary';
    commitBtn.textContent = 'Import';
    commitBtn.setAttribute('disabled', 'true');
    commitBtn.addEventListener('click', async () => {
      if (!canCommit) return;
      commitBtn.setAttribute('disabled', 'true');
      commitBtn.textContent = 'Importing…';
      try {
        const r = await api.importExtinguishers(textarea.value.trim(), true);
        Toast.success(`Imported ${r.created} extinguisher${r.created === 1 ? '' : 's'}. Print their QR labels from each location.`);
        modal.close();
        await this.loadExtinguishers();
      } catch (err) {
        const e = err as Error & { code?: string; upgradeUrl?: string };
        Toast.error(e.message || 'Import failed');
        if (e.code === 'PLAN_LIMIT') {
          modal.close();
          router.navigate(e.upgradeUrl || '/billing');
        }
        commitBtn.removeAttribute('disabled');
        commitBtn.textContent = 'Import';
      }
    });
    footer.appendChild(cancel);
    footer.appendChild(commitBtn);

    modal.open({ title: 'Import extinguishers from a spreadsheet', content: wrap, footer, size: 'large' });
  }

  private async openAddExtinguisherFlow(): Promise<void> {
    await this.showAddExtinguisherModal();
  }

  /**
   * Show add extinguisher modal
   */
  private async showAddExtinguisherModal(): Promise<void> {
    const locations = await this.fetchLocations();
    if (locations.length === 0) {
      Toast.info('Add a location first, then add extinguishers to it.');
      router.navigate('/locations');
      return;
    }

    if (!this.addModal) {
      this.addModal = document.createElement('app-modal') as Modal;
      document.body.appendChild(this.addModal);
    }

    const form = buildExtinguisherForm({ locations, locationId: locations.length === 1 ? locations[0].id : undefined });
    const footer = document.createElement('div');
    footer.style.display = 'flex';
    footer.style.gap = '12px';
    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'btn-secondary';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', () => this.addModal?.close());
    const saveBtn = document.createElement('button');
    saveBtn.type = 'button';
    saveBtn.className = 'btn-primary';
    saveBtn.textContent = 'Add Extinguisher';
    saveBtn.addEventListener('click', async () => {
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }
      saveBtn.disabled = true;
      saveBtn.textContent = 'Adding…';
      try {
        await this.handleAddExtinguisher(form);
      } finally {
        saveBtn.disabled = false;
        saveBtn.textContent = 'Add Extinguisher';
      }
    });
    footer.appendChild(cancelBtn);
    footer.appendChild(saveBtn);

    this.addModal.open({ title: 'Add Fire Extinguisher', content: form, footer, size: 'medium' });
    setTimeout(() => (form.querySelector('#fg-qr') as HTMLInputElement | null)?.focus(), 50);
  }

  /**
   * Fetch locations from API
   */
  private async fetchLocations(): Promise<any[]> {
    try {
      const response = await api.getLocations();
      return response.locations || [];
    } catch (error) {
      console.error('Error fetching locations:', error);
      Toast.error('Failed to load locations');
      return [];
    }
  }

  /**
   * Handle add extinguisher form submission
   */
  private async handleAddExtinguisher(form: HTMLFormElement): Promise<void> {
    try {
      const created = await api.createExtinguisher(readExtinguisherForm(form));
      Toast.success(`Added ${created.qr_code}. Print its QR label from the location page.`);
      this.addModal?.close();
      await this.loadExtinguishers();
    } catch (error) {
      const err = error as Error & { code?: string; upgradeUrl?: string };
      if (err.code === 'PLAN_LIMIT') {
        Toast.error(err.message || 'Plan limit reached');
        this.addModal?.close();
        router.navigate(err.upgradeUrl || '/billing');
        return;
      }
      if (err.code === 'DUPLICATE_QR_CODE') {
        Toast.error('That QR code is already in use. Choose a different tag.');
        (form.querySelector('#fg-qr') as HTMLInputElement | null)?.focus();
        return;
      }
      Toast.error(err.message || 'Failed to add extinguisher');
    }
  }
}

// Register custom element
customElements.define('extinguisher-list-page', ExtinguisherListPage);
